import { List, useTable, EditButton, ShowButton, DateField } from "@refinedev/antd";
import { useUpdate, useApiUrl, useCustomMutation } from "@refinedev/core";
import {
  Table,
  Tag,
  Space,
  Typography,
  Input,
  Select,
  Card,
  Row,
  Col,
  Button,
  Tooltip,
  Progress,
  Popconfirm,
  Grid,
} from "antd";
import {
  TeamOutlined,
  CrownOutlined,
  StopOutlined,
  SearchOutlined,
  LoginOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import type { User, Plan, Role } from "@/lib/types";
import { StatCard } from "@/components/StatCard";
import { FREE_LIMIT, PREMIUM_LIMIT } from "@/lib/constants";

const { Text } = Typography;
const { useBreakpoint } = Grid;

/** User do mock acrescido de `banned`/`nome` (vide src/mock/fixtures.ts). */
type AdminUser = User & { banned?: boolean; nome?: string | null };

const PLANO_COLOR: Record<Plan, string> = { premium: "gold", free: "default" };

const quotaFor = (u: AdminUser): { used: number; limit: number; pct: number } => {
  const limit = u.plano === "premium" ? PREMIUM_LIMIT : FREE_LIMIT;
  const used = (u.registros_mes ?? 0) + (u.registros_extras ?? 0);
  return { used, limit, pct: Math.min(100, Math.round((used / limit) * 100)) };
};

/**
 * /admin/users — contas do MEL.
 *
 * Como obtém os dados (MOCK MODE): `useTable` lista os usuários paginados
 * server-like contra FIXTURES.users (filtros eq/contains aplicados pelo
 * mockDataProvider). Os filtros do topo (busca, plano, role, status) viram
 * `CrudFilters` enviados ao dataProvider — mesma semântica de
 * GET /admin/users?email_like=&plano=&role=&banned= quando a API real entrar.
 *
 * Ações administrativas (auditáveis na API):
 *   - Banir / Reativar  → useUpdate({ banned }) (PATCH /admin/users/:id)
 *   - Impersonate       → POST /admin/users/:id/impersonate (useCustomMutation)
 */
export const UserList: React.FC = () => {
  const screens = useBreakpoint();
  const [query, setQuery] = useState("");
  const [plano, setPlano] = useState<Plan | undefined>();
  const [role, setRole] = useState<Role | undefined>();
  const [status, setStatus] = useState<"active" | "banned" | undefined>();

  const { tableProps, setFilters } = useTable<AdminUser>({
    syncWithLocation: true,
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
  });

  // Empurra os controles do topo para o dataProvider (server-like).
  const applyFilters = (next: {
    query?: string;
    plano?: Plan | undefined;
    role?: Role | undefined;
    status?: "active" | "banned" | undefined;
  }) => {
    const q = next.query ?? query;
    const p = "plano" in next ? next.plano : plano;
    const r = "role" in next ? next.role : role;
    const s = "status" in next ? next.status : status;
    setFilters(
      [
        { field: "email", operator: "contains", value: q || undefined },
        { field: "plano", operator: "eq", value: p ?? undefined },
        { field: "role", operator: "eq", value: r ?? undefined },
        {
          field: "banned",
          operator: "eq",
          value: s === undefined ? undefined : s === "banned",
        },
      ],
      "replace",
    );
  };

  // KPIs do conjunto atualmente carregado na página (visão rápida).
  const pageRows = (tableProps.dataSource ?? []) as AdminUser[];
  const kpis = useMemo(() => {
    const total = tableProps.pagination
      ? (tableProps.pagination as { total?: number }).total ?? pageRows.length
      : pageRows.length;
    return {
      total,
      premium: pageRows.filter((u) => u.plano === "premium").length,
      banned: pageRows.filter((u) => u.banned).length,
    };
  }, [pageRows, tableProps.pagination]);

  // ── Ações ──────────────────────────────────────────────────────────────────
  const { mutate: updateUser } = useUpdate();
  const setBanned = (row: AdminUser, banned: boolean) =>
    updateUser({
      resource: "users",
      id: row.id,
      values: { banned },
      mutationMode: "optimistic",
      successNotification: () => ({
        type: "success",
        message: banned ? "Usuário banido" : "Usuário reativado",
        description: row.email,
      }),
    });

  const apiUrl = useApiUrl();
  const { mutate: impersonate, isLoading: impersonating } = useCustomMutation();
  const doImpersonate = (row: AdminUser) =>
    impersonate({
      url: `${apiUrl}/users/${row.id}/impersonate`,
      method: "post",
      values: {},
      successNotification: () => ({
        type: "success",
        message: "Sessão de impersonação criada",
        description: `Você está agindo como ${row.email}. Ação registrada no audit log.`,
      }),
      errorNotification: () => ({
        type: "error",
        message: "Não foi possível impersonar",
        description: "Verifique permissões e o audit log.",
      }),
    });

  return (
    <List title="Usuários">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <StatCard
            title="Contas (total)"
            value={kpis.total}
            prefix={<TeamOutlined />}
            hint="usuários cadastrados"
          />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard
            title="Premium (página)"
            value={kpis.premium}
            prefix={<CrownOutlined />}
            hint="assinantes nesta página"
          />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard
            title="Banidos (página)"
            value={kpis.banned}
            prefix={<StopOutlined />}
            hint="contas suspensas nesta página"
          />
        </Col>
      </Row>

      <Card className="mel-glass" bordered={false} style={{ marginBottom: 16 }}>
        <Space wrap size={12} style={{ width: "100%" }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar por e-mail…"
            style={{ width: screens.xs ? "100%" : 280 }}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              applyFilters({ query: e.target.value });
            }}
          />
          <Select
            allowClear
            placeholder="Plano"
            style={{ width: 150 }}
            value={plano}
            onChange={(v) => {
              setPlano(v);
              applyFilters({ plano: v });
            }}
            options={[
              { value: "premium", label: "Premium" },
              { value: "free", label: "Free" },
            ]}
          />
          <Select
            allowClear
            placeholder="Role"
            style={{ width: 150 }}
            value={role}
            onChange={(v) => {
              setRole(v);
              applyFilters({ role: v });
            }}
            options={[
              { value: "user", label: "user" },
              { value: "admin", label: "admin" },
            ]}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 150 }}
            value={status}
            onChange={(v) => {
              setStatus(v);
              applyFilters({ status: v });
            }}
            options={[
              { value: "active", label: "Ativo" },
              { value: "banned", label: "Banido" },
            ]}
          />
        </Space>
      </Card>

      <Card className="mel-glass" bordered={false}>
        <Table {...tableProps} rowKey="id" scroll={{ x: 1100 }}>
          <Table.Column<AdminUser>
            title="Conta"
            dataIndex="email"
            sorter
            render={(email: string, row) => (
              <Space direction="vertical" size={0}>
                <Space size={6}>
                  <Text strong>{row.nome ?? email}</Text>
                  {row.banned && <Tag color="red">banido</Tag>}
                  {!row.email_verified && <Tag color="orange">não verificado</Tag>}
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {email} · {row.id}
                </Text>
              </Space>
            )}
          />
          <Table.Column
            dataIndex="role"
            title="Role"
            render={(role: Role) => (
              <Tag color={role === "admin" ? "geekblue" : "default"}>{role}</Tag>
            )}
          />
          <Table.Column
            dataIndex="plano"
            title="Plano"
            render={(plano: Plan) => (
              <Tag color={PLANO_COLOR[plano] ?? "default"}>{plano}</Tag>
            )}
          />
          <Table.Column<AdminUser>
            title="Uso (mês)"
            dataIndex="registros_mes"
            sorter
            render={(_, row) => {
              const q = quotaFor(row);
              return (
                <Tooltip
                  title={`${q.used} de ${q.limit} registros (${q.pct}%)`}
                >
                  <Space direction="vertical" size={2} style={{ width: 140 }}>
                    <Text style={{ fontSize: 12 }}>
                      {q.used}
                      <Text type="secondary"> / {q.limit}</Text>
                    </Text>
                    <Progress
                      percent={q.pct}
                      showInfo={false}
                      size="small"
                      status={q.pct >= 100 ? "exception" : "active"}
                      strokeColor={q.pct >= 100 ? undefined : "#06B6D4"}
                    />
                  </Space>
                </Tooltip>
              );
            }}
          />
          <Table.Column
            dataIndex="provider"
            title="Auth"
            render={(p: User["provider"]) => <Tag>{p}</Tag>}
          />
          <Table.Column
            dataIndex="created_at"
            title="Criado"
            sorter
            render={(v: string) => <DateField value={v} format="DD/MM/YYYY" />}
          />
          <Table.Column<AdminUser>
            title="Ações"
            fixed="right"
            width={220}
            render={(_, row) => (
              <Space size={4} wrap>
                <ShowButton hideText size="small" recordItemId={row.id} />
                <EditButton hideText size="small" recordItemId={row.id} />
                {row.banned ? (
                  <Popconfirm
                    title="Reativar conta?"
                    description="O usuário voltará a ter acesso ao MEL."
                    okText="Reativar"
                    cancelText="Cancelar"
                    onConfirm={() => setBanned(row, false)}
                  >
                    <Button
                      size="small"
                      icon={<CheckCircleOutlined />}
                      type="text"
                      style={{ color: "#10B981" }}
                    >
                      Reativar
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="Banir esta conta?"
                    description="Bloqueia o acesso. Ação registrada no audit log."
                    okText="Banir"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancelar"
                    onConfirm={() => setBanned(row, true)}
                    disabled={row.role === "admin"}
                  >
                    <Button
                      size="small"
                      icon={<StopOutlined />}
                      type="text"
                      danger
                      disabled={row.role === "admin"}
                    >
                      Banir
                    </Button>
                  </Popconfirm>
                )}
                <Tooltip
                  title={
                    row.role === "admin"
                      ? "Não é possível impersonar outro admin"
                      : "Abrir sessão como este usuário (auditado)"
                  }
                >
                  <Button
                    size="small"
                    type="text"
                    icon={<LoginOutlined />}
                    loading={impersonating}
                    disabled={row.role === "admin" || row.banned}
                    onClick={() => doImpersonate(row)}
                  />
                </Tooltip>
              </Space>
            )}
          />
        </Table>
      </Card>
    </List>
  );
};
